import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Spinner } from '@chakra-ui/react'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { auth } from '../../config/firebase'
import { onAuthStateChanged } from 'firebase/auth'

interface OnboardingRouteGuardProps {
  children: React.ReactNode
  requiredStep?: number
  defaultMode?: 'service' | 'product'
}

export const OnboardingRouteGuard: React.FC<OnboardingRouteGuardProps> = ({
  children,
  requiredStep,
  defaultMode
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  // useOnboarding will throw if context is not available, which is what we want
  // This ensures we never proceed if context isn't ready
  const { step, loadProgress, setOnboardingMode, resetState } = useOnboarding()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [initializing, setInitializing] = useState(true)
  const hasLoadedProgressRef = useRef(false)

  const getModeFromPath = (pathname: string): 'service' | 'product' | null => {
    if (pathname.startsWith('/for-products/onboarding')) return 'product'
    if (pathname.startsWith('/for-services/onboarding')) return 'service'
    return null
  }

  // Sync onboarding mode from path only
  useEffect(() => {
    if (!setOnboardingMode) return
    const mode = getModeFromPath(location.pathname) || defaultMode || null
    if (mode) setOnboardingMode(mode)
  }, [location.pathname, defaultMode, setOnboardingMode])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken()
          localStorage.setItem('firebase_id_token', token)
          setIsAuthenticated(true)
          // Load progress once per auth session to avoid loops
          // Pass the current user to ensure we use the correct user's info, not stale data
          if (!hasLoadedProgressRef.current) {
            hasLoadedProgressRef.current = true
            try {
              await loadProgress(user)
            } finally {
              setInitializing(false)
            }
          } else {
            setInitializing(false)
          }
        } catch (error) {
          console.error('Failed to get token:', error)
          setIsAuthenticated(false)
          setInitializing(false)
        }
      } else {
        setIsAuthenticated(false)
        hasLoadedProgressRef.current = false
        setInitializing(false)
        // Clear onboarding state on logout to prevent stale data from persisting
        resetState()
        // Redirect to get-started if not authenticated
        // Only redirect if we're actually on an onboarding page to prevent loops
        const currentPath = window.location.pathname
        if (
          (currentPath.startsWith('/for-services/onboarding') || currentPath.startsWith('/for-products/onboarding')) &&
          !currentPath.endsWith('/get-started')
        ) {
          const mode = getModeFromPath(currentPath) || defaultMode
          const getStartedPath =
            mode === 'product'
              ? '/for-products/get-started'
              : mode === 'service'
                ? '/for-services/get-started'
                : '/get-started'

          navigate(getStartedPath, { state: { from: currentPath }, replace: true })
        }
      }
    })

    return () => unsubscribe()
    // Only depend on stable references to avoid re-subscribing on each state update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, loadProgress, defaultMode])

  // Check step requirements
  useEffect(() => {
    // Only check step requirements if we have a valid step value
    if (isAuthenticated && !initializing && requiredStep !== undefined && step !== undefined && step !== null) {
      // Determine path prefix from route
      const pathPrefix = location.pathname.startsWith('/for-products/onboarding') 
        ? '/for-products/onboarding' 
        : '/for-services/onboarding'

      const routeStepMap: Record<string, number> = {
        '/for-services/onboarding/basic-setup': 0,
        '/for-services/onboarding/preview': 1,
        '/for-services/onboarding/landing-page': 2,
        '/for-services/onboarding/details': 2,
        '/for-services/onboarding/package-selection': 3,
        '/for-products/onboarding/basic-setup': 0,
        '/for-products/onboarding/preview': 1,
        '/for-products/onboarding/landing-page': 2,
        '/for-products/onboarding/details': 2,
        '/for-products/onboarding/package-selection': 3,
        '/dashboard': 5
      }

      const currentPath = location.pathname
      const currentRouteStep = routeStepMap[currentPath] ?? 0

      // Redirect if user hasn't reached the required step for this route
      if (step !== undefined && step < requiredStep) {
        const stepDifference = requiredStep - step
        
        // When step is 0 (no onboarding data yet), always redirect to basic-setup
        // This prevents accessing preview/landing-page/etc. before completing basic setup
        if (step === 0 && requiredStep > 0) {
          const redirectTo = `${pathPrefix}/basic-setup`
          if (currentPath !== redirectTo) {
            navigate(redirectTo, { replace: true })
          }
        }
        // Allow forward progression: if user is on step N (N > 0), they can access step N+1
        // Only block if step is more than 1 step behind required
        else if (stepDifference > 1) {
          // User is more than 1 step behind, redirect to their current step
          const stepRoutes = [
            `${pathPrefix}/basic-setup`,
            `${pathPrefix}/preview`,
            `${pathPrefix}/landing-page`,
            `${pathPrefix}/package-selection`,
            `${pathPrefix}/details`,
            `${pathPrefix}/assets`
          ]
          const redirectTo = stepRoutes[step] || `${pathPrefix}/basic-setup`
          // Only navigate if we're not already on the target route
          if (currentPath !== redirectTo) {
            navigate(redirectTo, { replace: true })
          }
        }
        // If stepDifference === 1 and step > 0, allow access (forward progression)
      }
    }
  }, [isAuthenticated, initializing, step, requiredStep, location.pathname, navigate])

  if (isAuthenticated === null || initializing) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" color="brand.500" />
      </Box>
    )
  }

  if (!isAuthenticated) {
    return null // Will redirect
  }

  // At this point, context is guaranteed to be available (useOnboarding would have thrown otherwise)
  return <>{children}</>
}
